/**
 * iter164 — Global PDF Preview event bus.
 *
 * Tüm PDF butonları indirmek yerine önizleme modal'ı açar.
 * iOS Safari: native PDF preview desteklemez → yeni sekme fallback
 * (iter146 pattern korunur).
 *
 * Kullanım:
 *   import { openPdfPreview } from "@/lib/pdfPreview";
 *   const blob = await api.getBlob("/gmp/cleaning/pdf?year=2024&month=3");
 *   openPdfPreview(blob, "gmp_temizlik_2024_03.pdf");
 *
 * api.download / api.downloadFile PDF filename gördüklerinde otomatik olarak
 * openPdfPreview'a yönlendirir — mevcut çağrı yerlerinin çoğu değişmeden çalışır.
 */

const EVENT_NAME = "pdf-preview:open";

/**
 * iter255: Detect iOS/iPadOS AND modern tablet environments where the inline
 * iframe PDF preview is unreliable. Modern iPadOS reports as `MacIntel` and
 * hides `iPad` from the UA, so we also probe `platform + maxTouchPoints`.
 * Standalone/PWA mode is a separate signal because installed-app WebViews
 * often lack native PDF renderers.
 */
export function isIosSafari() {
  const nav = typeof navigator !== "undefined" ? navigator : {};
  const ua = nav.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return true;
  // iPadOS 13+ masquerades as Mac. Only real touch-capable "Macs" (i.e. iPads)
  // have >1 touch points — desktop Macs report 0.
  if (nav.platform === "MacIntel" && (nav.maxTouchPoints || 0) > 1) return true;
  // iter257: extend to non-iOS mobile/tablet Chrome/Firefox where inline
  // iframe PDF previews are commonly disabled by the WebView (Android
  // WebView, Chrome mobile without integrated viewer, Firefox mobile).
  if (/Android/i.test(ua)) return true;
  // Coarse-pointer + narrow viewport is a reliable "no inline PDF" signal.
  try {
    const coarse = window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
    const narrow = window.innerWidth && window.innerWidth <= 1024;
    if (coarse && narrow) return true;
  } catch { /* noop */ }
  return false;
}

/** iter255: Standalone/PWA detection — installed-app WebViews often lack an
 * inline PDF viewer and iframe preview fails silently. In that case we fall
 * back to native new-tab/blob navigation. */
function isStandalonePWA() {
  try {
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
    if (typeof navigator !== "undefined" && navigator.standalone === true) return true;
  } catch { /* noop */ }
  return false;
}

/** iter255: Combined signal — any environment where the inline iframe preview
 * should be avoided. Desktop browsers keep the rich modal preview. */
export function shouldUseNativePdfViewer() {
  return isIosSafari() || isStandalonePWA();
}

export function isPdfFilename(name) {
  return typeof name === "string" && name.toLowerCase().endsWith(".pdf");
}

/** Blob'u açıkça "application/pdf" tipine normalize eder — bazı tarayıcılar
 * için gerekli (iter146 pattern). */
function normalizeBlob(blob) {
  if (blob && blob.type === "application/pdf") return blob;
  try {
    return new Blob([blob], { type: "application/pdf" });
  } catch {
    return blob;
  }
}

/**
 * PDF önizleme dialog'unu açar.
 * @param {Blob} blob — PDF verisi
 * @param {string} filename — İndir butonu için önerilen dosya adı
 */
export function openPdfPreview(blob, filename) {
  const pdfBlob = normalizeBlob(blob);
  const objectUrl = URL.createObjectURL(pdfBlob);

  // iter257: iPadOS/iOS/PWA/mobile — `window.open` is unreliable in Safari
  // (blob URLs can be silently blocked outside a direct user gesture).
  // Programmatically clicking an `<a>` element preserves the user-gesture
  // token through the async fetch, so the native PDF viewer opens
  // consistently. Fallback: same-window navigation if the click was
  // still suppressed (rare, only in embedded WebViews).
  if (shouldUseNativePdfViewer()) {
    try {
      const a = document.createElement("a");
      a.href = objectUrl;
      a.target = "_blank";
      a.rel = "noopener";
      // iOS Safari respects `download` attribute only when the user
      // explicitly wants download; leave it unset so the viewer opens.
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      window.location.href = objectUrl;
    }
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    return;
  }

  window.dispatchEvent(
    new CustomEvent(EVENT_NAME, {
      detail: { objectUrl, filename: filename || "document.pdf" },
    })
  );
}

export function subscribePdfPreview(handler) {
  const listener = (e) => handler(e.detail);
  window.addEventListener(EVENT_NAME, listener);
  return () => window.removeEventListener(EVENT_NAME, listener);
}
